import { FormActions } from "../../components/common/FormActions";
import { BooleanTags, StatusTag } from "../../components/common/registerUtils";
import { API_FRAUD_WEIGHT_SCORE } from "../../utils/apis";
import {
  createEditAction,
  createViewAction,
  formatDateTime,
  getForm,
  getRegister,
  hasWorkflow,
} from "../../utils/helpers";
import { URL_FRAUD_WEIGHTSCORE } from "../../utils/urls";
import { IFormSection } from "../../types/common";
import { useAuth } from "../../context/AuthContext";
import { APPROVE, FRAUD_WEIGHTSCORE, REJECT } from "../../utils/constants";
import { Send } from "@mui/icons-material";
import useSet from "../common/useSet";
import toast from "react-hot-toast";
import { useToasts } from "../common/useToasts";

const url = URL_FRAUD_WEIGHTSCORE;

// REGISTER
export const useFraudWeightScore = () => {
  const { user } = useAuth();
  const { uploadData, success, submitting, error } = useSet();
  const {
    uploadData: uploadApprovalData,
    success: approveSuccess,
    error: approveError,
    submitting: approving,
  } = useSet();
  const {
    uploadData: uploadRejectData,
    success: rejectSuccess,
    error: rejectError,
    submitting: rejecting,
  } = useSet();

  useToasts({
    success,
    error,
    submitting,
    successMessage: "Forwarded successfully",
  });

  useToasts({
    success: rejectSuccess,
    error: rejectError,
    submitting: rejecting,
    successMessage: "Rejected successfully",
  });

  useToasts({
    success: approveSuccess,
    error: approveError,
    submitting: approving,
    successMessage: " Approved successfully",
  });

  const handleApprove = async (item: any) => {
    try {
      await uploadApprovalData({
        api: `${API_FRAUD_WEIGHT_SCORE}${item?.id}/`,
        method: "PATCH",
        params: {
          status: "approved",
          current_scrutiny_level: item?.current_scrutiny_level + 1,
        },
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Approval failed");
    }
  };

  const handleReject = async (id: any, reason?: string) => {
    try {
      await uploadRejectData({
        api: `${API_FRAUD_WEIGHT_SCORE}${id}/`,
        method: "PATCH",
        params: {
          status: "rejected",
          rejected_reason: reason,
        },
      });
    } catch (error: any) {
      console.error("Rejection Error:", error);
      toast.error(error?.response?.data?.detail || "Rejected failed");
    }
  };

  const handleForward = async (item) => {
    try {
      await uploadData({
        api: `${API_FRAUD_WEIGHT_SCORE}${item?.id}/`,
        method: "PATCH",
        params: {
          current_scrutiny_level: item?.current_scrutiny_level + 1,
        },
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Approval failed");
    }
  };

  const tableColumns = () => [
    { id: "factor_type", header: "Factor Type", filter: {} },
    { id: "weight", header: "Weight", filter: {} },
    { id: "description", header: "description", filter: {} },
    { id: "status", header: "Status", filter: {} },
    {
      id: "is_active",
      header: "Is Active",
      filter: {
        type: "select",
        placeholder: "Select status...",
        options: [
          { id: "false", name: "False" },
          { id: "true", name: "True" },
        ],
      },
    },
    { id: "created_at", header: "Created At", filter: {} },
    { id: "created_by", header: "Created By", filter: {} },
    { id: "actions", header: "Actions" },
  ];
  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        is_active: <BooleanTags status={item?.is_active} />,
        status: <StatusTag status={item?.status} />,
        weight: item?.weight,
        created_at: formatDateTime(item?.created_at),
        created_by: `${item?.created_by?.first_name} ${item?.created_by?.last_name}`,
        actions: (
          <FormActions
            actions={[
              createViewAction(url, item?.id),
              createEditAction(url, item?.id),
              ...(hasWorkflow(user?.workflows, FRAUD_WEIGHTSCORE, 1) &&
              item?.current_scrutiny_level === 1
                ? [
                    {
                      label: `Forward to level ${item.current_scrutiny_level + 1}`,
                      custom: true,
                      icon: Send,
                      showReasonField: false,
                      deleteMessage:
                        "Please provide a reason for rejecting this credit.",
                      handleCustom: () => handleForward(item),
                    },
                  ]
                : []),
              ...(item?.status === "pending" &&
              hasWorkflow(user?.workflows, FRAUD_WEIGHTSCORE, 2) &&
              item?.current_scrutiny_level === 2
                ? [
                    {
                      label: APPROVE,
                      custom: true,
                      showReasonField: false,
                      handleCustom: () => handleApprove(item),
                    },
                    {
                      label: REJECT,
                      custom: true,
                      showReasonField: true,
                      deleteMessage:
                        "Please provide a reason for rejecting this weight score.",
                      handleCustom: () => handleReject(item?.id),
                    },
                  ]
                : []),
            ]}
          />
        ),
      };
    }),
  ];
  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Fraud Weight Score Details",
          fields: {
            id: item?.id,
            factor_type: item?.factor_type,
            weight: item?.weight,
            description: item?.description,
            is_active: <BooleanTags status={item?.is_active} />,
            status: <StatusTag status={item?.status} />,
            current_scrutiny_level: item?.current_scrutiny_level,
            created_at: formatDateTime(item?.created_at),
            created_by: `${item?.created_by?.first_name} ${item?.created_by?.last_name}`,
          },
        },
      ],
    };
  };

  // const formRoute = getForm(url);
  // const activityFilter = "object_id";
  // const filters = {
  //   model_name: "FraudScoreWeightConfig",
  // };
  const formRoute = hasWorkflow(user?.workflows, FRAUD_WEIGHTSCORE, 1)
    ? getForm(url)
    : null;

  return {
    tableData,
    tableColumns,
    formRoute,
    viewData,
    // activityFilter,
    // filters,
  };
};

// PARTNERS
export const useFraudWeightScoreForm = ({ id }) => {
  const isEdit = !!id;
  const sections: IFormSection[] = [
    {
      title: "Fraud Weight Score Details",
      fields: [
        {
          field_name: "factor_type",
          type: "select",
          description:
            "Select the fraud risk factor you want to assign a weight to. Each factor influences the overall fraud risk score.",
          props: { required: !isEdit },
          options_data: [
            { id: "velocity", name: "Velocity" },
            { id: "amount", name: "Amount" },
            { id: "geo", name: "Geo" },
            { id: "behavior", name: "Behavior" },
            { id: "device", name: "Device" },
            { id: "historical", name: "Historical" },
          ],
        },
        {
          field_name: "weight",
          type: "number",
          description:
            "Enter the numeric weight assigned to this factor. Higher values increase the overall fraud risk score.",
          props: { required: !isEdit },
        },
        {
          field_name: "description",
          type: "text",
          description:
            "Provide a short explanation of how this factor contributes to the fraud score.",
          props: { required: !isEdit },
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  function onSubmit(payload: any, id: string | number) {
    const params = {
      ...payload,
    };
    if (id) {
      return {
        id,
        api: `${API_FRAUD_WEIGHT_SCORE}`,
        params: params,
      };
    }

    //   CREATE
    return {
      api: `${API_FRAUD_WEIGHT_SCORE}`,
      params: params,
    };
  }

  return { sections, redirect, onSubmit };
};
